/* eslint-disable no-useless-escape */
import React, { useEffect, useState, useRef, forwardRef } from "react";
import propTypes from "prop-types";
import classnames from "classnames";

import { useField } from "../form/Form";

import { createDataAttributes } from "../../utilities";
import Button from "../button/Button";
import ErrorsForField from "../form/ErrorsForField";
import Label from "./Label";
import LoadingIndicator from "../loading-indicator/LoadingIndicator";
import "./InputApiCall.scss";

const InputSearchMenu = forwardRef(function (
  { id, options, setShow, mainRef, onSelect, isSearching, optionListFullWidth },
  menuRef
) {
  const [pos, setPos] = useState({});

  // handle clicking outside of the menu to close the menu
  useEffect(() => {
    const handleClickOutside = event => {
      if (mainRef.current && !mainRef.current.contains(event.target)) {
        setShow(false);
      }
    };
    const escKey = event => {
      const e = event || window.event;
      if (e.key === "Escape") {
        e.preventDefault();
        setShow(false);
      }
    };
    document.addEventListener("keydown", escKey, true);
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
      document.removeEventListener("keydown", escKey, true);
    };
  }, [mainRef, setShow]);

  // handle the position of the menu in case it doesn't fit in the viewport when the component renders
  useEffect(() => {
    if (mainRef?.current && menuRef?.current) {
      const mainRect = mainRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - (mainRect?.bottom || 0);
      const menuHeight = menuRect?.height;
      const showAbove = menuHeight && spaceBelow < menuHeight;
      const newPos = {
        top: showAbove
          ? "-" + (menuRect.height + 10) + "px"
          : mainRect.height + 5 + "px"
      };

      if (isSearching || optionListFullWidth) {
        const inpt = mainRef.current.querySelector("input");
        const inputRect = inpt ? inpt.getBoundingClientRect() : {};

        newPos.width = inputRect?.width + "px";
      } else {
        // use this if we want to auto-focus after the search is complete
        // disabled for now because it feels like it interupts the user too much if we search if they pause for a second
        // menuRef.current.querySelector("button").focus();
      }
      if (mainRect && menuRect) setPos(newPos);
    }
  }, [mainRef, menuRef, isSearching, optionListFullWidth]);

  // allow select when navigating with keys
  const upDown = (e, i, o) => {
    if (e.key === "Tab") return;

    e.preventDefault();
    if (e.key === "Enter" || e.key === "Return") {
      onSelect(o);
      setShow(false);
    } else if (e.key === "ArrowDown" && i !== options.length - 1)
      menuRef.current.querySelectorAll("button")[i + 1].focus();
    else if (e.key === "ArrowUp" && i > 0)
      menuRef.current.querySelectorAll("button")[i - 1].focus();
    else if (e.key === "ArrowUp" && i === 0)
      mainRef.current.querySelector("input").focus();
    else if (e.key === "Escape") {
      setShow(false);
      mainRef.current.querySelector("input").focus();
    } else if (e.key === "Home")
      menuRef.current.querySelectorAll("button")[0].focus();
    else if (e.key === "End")
      menuRef.current.querySelectorAll("button")[options.length].focus();
  };

  return (
    <div
      aria-labelledby={id}
      role="listbox"
      ref={menuRef}
      className={classnames("uikit__InputApiCall__menu")}
      style={pos}
    >
      {isSearching ? (
        <div className="uikit__InputApiCall__menu__searching">Searching...</div>
      ) : (
        <>
          {options.map((o, i) => (
            <button
              role="option"
              aria-selected="false"
              key={o.id || o.key || o.label}
              // allow options to pass in a custom classname in the object data
              // make sure the css is clean and doesn't include invalid css characters
              className={classnames("uikit__InputApiCall__menu__item", {
                [`${(o?.optionClassName || "").replace(
                  /\s|~|@|#|$|%|^\&|\*|\(|\)|{|}|\||\\|\[|\]|\?|\/|<|>|\"|\'|\+/g,
                  "_"
                )}`]: o?.optionClassName
              })}
              onClick={event => {
                event.stopPropagation();
                event.preventDefault();
                onSelect(o);
                setShow(false);
              }}
              onKeyDown={e => {
                upDown(e, i, o);
              }}
            >
              {o.label}
            </button>
          ))}
        </>
      )}
    </div>
  );
});

const InputApiCall = ({
  className,
  dataAttrib,
  disabled,
  id,
  searchFunction,
  size,
  name,
  label,
  labelElement,
  value,
  placeholder,
  onChange,
  onBlur,
  onKeyDown,
  onValidate,
  required,
  hideLabel,
  noLabel,
  fieldErrors,
  hideErrors,
  requiredError,
  dataWrapperAttrib,
  debounceTime,
  optionListFullWidth,
  onSearch,
  onOptionSelect,
  showOptions,
  onReset,
  ...rest
}) => {
  const mainRef = useRef(null);
  const menuRef = useRef(null);

  const [show, setShow] = useState(false);
  const [searchStr, setSearchStr] = useState("");
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState();
  const [isSearching, setIsSearching] = useState();
  const [inputFocused, setInputFocused] = useState();

  const timeout = useRef();
  const loadOptionsWithDebounce = async value => {
    setIsSearching(true);
    clearTimeout(timeout?.current?.call);
    timeout.current = {
      call: setTimeout(async () => {
        const opts = await searchFunction(value);

        /**
         * check to see if there's a more recent request by comparing the
         * search string we sent to the search function...
         * if they're different, that means we got a new search request while waiting for the first request...
         * so we'll ignore the first request
         */
        if (searchStr === timeout?.current?.searchStr) {
          // send results to custom function if it exists
          if (onSearch) onSearch(opts);

          if (
            showOptions &&
            opts?.every(
              i => i.hasOwnProperty("label") && i.hasOwnProperty("value")
            )
          ) {
            setOptions(opts);
            if (opts && opts.length > 0) setShow(true);
          }

          setIsSearching(false);
        }
      }, debounceTime),
      searchStr
    };
  };

  /** using an effect here because it's more flexible than only
   * inlcuding it in the search response in the func above
   */
  useEffect(() => {
    if (
      showOptions &&
      !isSearching &&
      !!searchStr &&
      (!options || options.length === 0) &&
      !value
    ) {
      setOptions([{ label: "No results found", value: "" }]);
      setShow(true);
    }
  }, [isSearching, searchStr, options, setOptions, showOptions, value]);

  const handleSearchChange = event => {
    const value = event.currentTarget.value;
    setSearchStr(value);
    if (!!value) loadOptionsWithDebounce(value);
    else {
      // stop existing search
      setIsSearching(false);
      clearTimeout(timeout?.current?.call);
      setOptions([]);
      setShow(false);
    }
  };

  const handleSearchBlur = () => {
    setInputFocused(false);

    // eslint-disable-next-line no-restricted-globals
    if (onBlur) onBlur({ event, field: name, value });
  };

  const handleSearchKeyDown = event => {
    if (show && event.key === "ArrowDown") {
      event.preventDefault();
      const firstItem = menuRef?.current?.querySelector("button");
      if (firstItem) firstItem.focus();
    } else if (!show && options.length) {
      event.preventDefault();
      setShow(false);
    }
    if (show && event.key === "Escape") setShow(false);
    if (onKeyDown) onKeyDown({ event, value });
  };

  const handleFocus = () => {
    setInputFocused(true);
    if (options.length > 0) setShow(true);
  };

  const resetSearch = () => {
    setSearchStr("");
    setOptions([]);
    setSelectedOption(null);
    setShow(false);
    onChange({
      field: name,
      value: "",
      callback: e => {
        if (onOptionSelect)
          setTimeout(onOptionSelect({ ...e, field: name, option: null }));
      }
    });
    setTimeout(
      () => mainRef.current && mainRef.current.querySelector("input").focus()
    );

    if (onReset) onReset();
  };

  const handleOptionSelect = option => {
    setOptions([]);
    setSelectedOption(option);
    setSearchStr("");
    setShow(false);
    onChange({
      // eslint-disable-next-line no-restricted-globals
      event,
      field: name,
      value: option?.value || "",
      callback: e => {
        if (onOptionSelect)
          setTimeout(onOptionSelect({ ...e, field: name, option }));
      }
    });
  };

  const handleOnValidate = ({ field, value, validation }) => {
    const errors = onValidate({ field, value, validation });
    return errors;
  };

  /** reset the search and option selection if value is reset */
  useEffect(() => {
    if (!value || value === "") {
      setSearchStr("");
      setSelectedOption(null);
    }
  }, [value]);

  // -------------------------
  //     FORM REGISTRATION
  // -------------------------

  useField({
    name,
    validation: { required, requiredError },
    onValidate: handleOnValidate,
    label
  });

  // -------------------------
  //     STYLES/CLAASES
  // -------------------------
  const sizeClass = size ? "uikit__InputApiCall-size__" + size : null;

  const containerClasses = classnames(
    "uikit__form-item",
    "uikit__InputApiCall-wrapper",
    className,
    sizeClass,
    { "uikit__hide-label": hideLabel, "uikit__form__hide-errors": hideErrors }
  );

  const inputClass = classnames({
    "has-error": !!fieldErrors?.length,
    "is-required": required,
    "has-value": !!value
  });

  return (
    <div
      className={containerClasses}
      {...createDataAttributes(dataWrapperAttrib)}
    >
      {!noLabel && (
        <Label
          htmlFor={id}
          label={label}
          labelElement={
            labelElement || isSearching ? (
              <LoadingIndicator
                className="uikit__InputApiCall__searching-spinner"
                type="spinner"
                message="Searching"
              />
            ) : (
              <></>
            )
          }
          required={required}
        />
      )}
      <div
        className={classnames("uikit__InputApiCall", {
          uikit__InputApiCall__isSearching: isSearching
        })}
      >
        <div ref={mainRef} className="uikit__InputApiCall__input-wrapper">
          <input
            aria-describedby={
              !hideErrors && !!fieldErrors?.length && `errors_for_${id}`
            }
            type="text"
            id={id}
            name={name}
            value={searchStr || value}
            className={inputClass}
            onChange={handleSearchChange}
            onBlur={handleSearchBlur}
            onFocus={handleFocus}
            onClick={handleFocus}
            onKeyDown={handleSearchKeyDown}
            placeholder={placeholder}
            disabled={!!selectedOption || disabled}
            {...createDataAttributes(dataAttrib)}
            {...rest}
          />
          {(show || isSearching) && showOptions && (
            <InputSearchMenu
              ref={menuRef}
              id={id}
              options={options}
              mainRef={mainRef}
              setShow={setShow}
              onSelect={handleOptionSelect}
              isSearching={isSearching}
              optionListFullWidth={optionListFullWidth}
            />
          )}
          {(!!searchStr || !!value) && (
            <span className="uikit__InputApiCall__reset">
              <Button isLink variant="info" onClick={resetSearch}>
                Reset
              </Button>
            </span>
          )}
        </div>

        {!inputFocused && !show && !hideErrors && !!fieldErrors?.length && (
          <ErrorsForField id={`errors_for_${id}`} errors={fieldErrors} />
        )}
      </div>
    </div>
  );
};

InputApiCall.propTypes = {
  className: propTypes.string,
  disabled: propTypes.bool,

  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  labelElement: propTypes.element,
  name: propTypes.string.isRequired,
  value: propTypes.oneOfType([propTypes.string, propTypes.number]).isRequired,
  onBlur: propTypes.func.isRequired,
  onChange: propTypes.func.isRequired,
  onKeyDown: propTypes.func,
  onValidate: propTypes.func,
  required: propTypes.bool,
  hideLabel: propTypes.bool,
  noLabel: propTypes.bool,
  fieldErrors: propTypes.array,
  hideErrors: propTypes.bool,
  requiredError: propTypes.string,
  placeholder: propTypes.string,
  debounceTime: propTypes.number,
  searchFunction: propTypes.func.isRequired,
  onSearch: propTypes.func,
  onOptionSelect: propTypes.func,
  onReset: propTypes.func,
  showOptions: propTypes.bool,
  optionListFullWidth: propTypes.bool,
  size: propTypes.oneOf([
    "auto",
    "full",
    "tiny",
    "sm",
    "md",
    "lg",
    "25p",
    "33p",
    "50p",
    "fill"
  ]),
  dataAttrib: propTypes.arrayOf(Object),
  dataWrapperAttrib: propTypes.arrayOf(Object),
  wrapperClassName: propTypes.string,
  componentname: propTypes.string
};

InputApiCall.defaultProps = {
  debounceTime: 1000,
  showOptions: true,
  disabled: false,
  size: "md",
  componentname: "InputApiCall"
};

export default InputApiCall;
